import { Box, ButtonV2, TextV2 } from '@withjoy/joykit';
import React, { useCallback, useMemo } from 'react';
import { useHistory } from '@react-router';
import type { GetProductCatalogHighlightedProductsByIdQuery } from './ProductGridHighlights.graphql.generated';
import { useGetProductCatalogHighlightedProductsByIdQuery } from './ProductGridHighlights.graphql.generated';
import { StarRatingWithLabel } from '../../../ProductDetails/components/ProductRating';
import { customRounding } from '@shared/utils/numbers';
import { FormattedCurrency } from '@shared/utils/currency';
import { StyledProductPhoto } from '../../../ProductTile/components';
import { withLazyPropOnIntersection } from '@shared/utils/hooks/useLazyLoadImage';
import { addRendition } from '@shared/utils/photoRendition';
import { useTranslation } from '@shared/core/i18n';
import ClampedText from '@apps/registry/common/components/ClampedText';
import qs from 'query-string';
import { useCatalogRouting } from '../../../../useCatalogRouting';
import { useProductGridHighlights } from './useProductGridHighlights';
import { HIGHLIGHTS } from './ProductGridHighlights.constants';
import {
  StyledProductGridHighlightsItem,
  StyledProductGridHighlightsItemImageWrapper,
  StyledProductGridHighlightsItemInfo,
  StyledProductGridHighlightsItemLink,
  StyledProductGridHighlightsItems,
  StyledProductGridHighlightsWrapper,
  FlexWrapper,
  styles,
} from './ProductGridHighlights.styles';

const LazyLoadingStyledProductPhoto = withLazyPropOnIntersection(StyledProductPhoto, 'photoUrl');

type CatalogItem = NonNullable<GetProductCatalogHighlightedProductsByIdQuery['productCatalogItemsByIds'][number]>;

export type ProductGridHighlightsProps = {
  positionIndex: 4 | 6 | 8 | 10 | 12;
};

export const ProductGridHighlights: React.FC<ProductGridHighlightsProps> = ({ positionIndex }) => {
  const { routeMatch, trackTileClick } = useProductGridHighlights();
  const { getCatalogItemPath } = useCatalogRouting();
  const { t } = useTranslation('sharedRegistry');
  const { itemButtonLabel } = t('subCatHighlights');
  const history = useHistory();
  const path = routeMatch?.path;

  const { data, loading } = useGetProductCatalogHighlightedProductsByIdQuery({
    variables: {
      ids: path === undefined ? '' : HIGHLIGHTS[path].map(({ productId }) => productId),
    },
    batchMode: 'fast',
    skip: !path,
  });

  const onScreenPhotoUrl = useCallback((displayPhotoUrl?: string) => {
    return addRendition({ url: displayPhotoUrl, renditionSize: 'small', isProduct: true });
  }, []);

  const generateItemPath = useCallback(
    (productId: string) => {
      return qs.stringifyUrl({
        url: getCatalogItemPath(productId),
        query: {
          source: 'productGridHighlights',
        },
      });
    },
    [getCatalogItemPath],
  );

  const handleOnItemClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>, product: CatalogItem, position: number) => {
      e.preventDefault();
      e.stopPropagation();

      trackTileClick({ product, position });

      history.push(generateItemPath(product.id));
    },
    [history, generateItemPath, trackTileClick],
  );

  const spacerCellCount = useMemo(() => {
    return [...Array(positionIndex / 2 - 1)].map((_, i) => i);
  }, [positionIndex]);

  const nonNullItems = useMemo(() => data?.productCatalogItemsByIds.filter(Boolean) || [], [data?.productCatalogItemsByIds]);

  if (!path || loading || nonNullItems.length <= 1) {
    return null;
  }

  return (
    <>
      <StyledProductGridHighlightsWrapper __css={styles.productHighlightsWrapper}>
        <StyledProductGridHighlightsItems __css={styles.productHighlightsItems}>
          {nonNullItems.map((product, position) => {
            if (!product) {
              return null;
            }

            return (
              <StyledProductGridHighlightsItem
                key={product.id}
                __css={styles.productHighlightsItem}
              >
                <TextV2 typographyVariant="HedMobile32">{HIGHLIGHTS[path][position].title}</TextV2>
                <StyledProductGridHighlightsItemLink
                  as="a"
                  role="group"
                  href={generateItemPath(product.id)}
                  onClick={e => handleOnItemClick(e, product, position)}
                  __css={styles.productHighlightsItemLink}
                >
                  <StyledProductGridHighlightsItemInfo
                    spacing={1}
                    __css={styles.productHighlightsItemInfo}
                  >
                    <TextV2
                      typographyVariant="label2"
                      color="mono10"
                    >
                      {product.brand?.name}
                    </TextV2>
                    <ClampedText
                      clampAt={2}
                      typographyVariant="hed2"
                      disableTooltip
                      wordBreak="break-word"
                      _groupHover={{
                        textDecoration: 'underline',
                      }}
                    >
                      {product.title}
                    </ClampedText>
                    <TextV2
                      typographyVariant="label2"
                      color="mono10"
                    >
                      <FormattedCurrency
                        priceFloatingPointDecimalString={product.price?.floatingPointDecimalString || '0'}
                        priceCurrencyCode={product.price?.currency?.code}
                        formatForm="short"
                        shouldCalculateCharmPrice={true}
                      />
                    </TextV2>
                    {product.rating && (
                      <StarRatingWithLabel
                        rating={customRounding(product.rating.stats.averageRating)}
                        totalReviewCount={product.rating.stats.totalReviewCount}
                        showPartialRating
                        labelVariant="totalReviewCountShort"
                        iconSize={16}
                      />
                    )}
                    {nonNullItems.length === 2 && <TextV2 typographyVariant="FeatureCopy17">{HIGHLIGHTS[path][position].description}</TextV2>}
                  </StyledProductGridHighlightsItemInfo>
                  <StyledProductGridHighlightsItemImageWrapper __css={styles.productHighlightsItemImageWrapper}>
                    <LazyLoadingStyledProductPhoto
                      photoUrl={{
                        onscreen: onScreenPhotoUrl(product.photos?.[0]?.url),
                      }}
                    />
                  </StyledProductGridHighlightsItemImageWrapper>
                </StyledProductGridHighlightsItemLink>
                <FlexWrapper flexDirection={['column', 'column', 'column-reverse']}>
                  {nonNullItems.length === 3 && (
                    <TextV2
                      typographyVariant="FeatureCopy17"
                      whiteSpace="pre-wrap"
                    >
                      {HIGHLIGHTS[path][position].description}
                    </TextV2>
                  )}
                  <ButtonV2
                    margin={6}
                    shape="rounded"
                    variant="muted"
                    width="90%"
                    type="button"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleOnItemClick(e, product, position)}
                  >
                    {itemButtonLabel()}
                  </ButtonV2>
                </FlexWrapper>
              </StyledProductGridHighlightsItem>
            );
          })}
        </StyledProductGridHighlightsItems>
      </StyledProductGridHighlightsWrapper>
      {/* Spacer needed to account for the amount of empty columns remaining after the main content to fix CSS inconsistencies */}
      {spacerCellCount.map(index => {
        return (
          <Box
            key={index}
            display="none"
          />
        );
      })}
    </>
  );
};
